import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom, distinctUntilChanged } from 'rxjs/operators';
import { GlobalService } from '../../../../SGRE-shared/services/global.service';
import * as ProductReferencePricingActions from '../action/product-reference-pricing.action';
import { selectProductReferences } from '../selector/product-reference-pricing.selector';
import { ApiService } from '../../../../SGRE-shared/services/api.service';

@Injectable()
export class ProductReferencePricingEffects {
  private processedReferences = new Set<string>();
  private currentProductCode: string = '';

  constructor(
    private actions$: Actions,
    private store: Store,
    private globalService: GlobalService,
    private apiService: ApiService
  ) { }

  loadPricesOnReferencesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductReferencePricingActions.loadProductsReferenceSuccess),
      mergeMap(action => {
        // Extract product codes to identify the current product context
        const currentProductCodes = ['OTHERS', 'SIMILAR']
          .flatMap(referenceType =>
            action.options[referenceType]?.flatMap(option => {
              // Check if baseOptions exists, otherwise use variantOptions
              const options = option.target?.baseOptions.at(0)?.options || option.target?.variantOptions;
              return options?.map(optionItem => optionItem.code) || [];
            }) || []
          );

        // Create a unique identifier for the current product context
        const productContext = currentProductCodes.sort().join(',');

        // If this is a different product context, clear processed references
        if (this.currentProductCode !== productContext) {
          this.processedReferences.clear();
          this.currentProductCode = productContext;
        }

        const actionKey = JSON.stringify(action.options);

        // Prevent duplicate processing for the same action
        if (this.processedReferences.has(actionKey)) {
          return of({ type: 'NO_ACTION' });
        }

        this.processedReferences.add(actionKey);

        if ('OTHERS' in action.options || 'SIMILAR' in action.options) {
          const productCodes = currentProductCodes.filter(code => code.endsWith('_N'));
          if (productCodes.length > 0) {
            // Delay is optional, remove if not needed
            return of(
              ProductReferencePricingActions.loadProductsReferencePrice({ productCodes })
            ).pipe();
          }
        }
        return of({ type: 'NO_ACTION' }); // No-op action
      })
    )
  );

  loadProductReferencePrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductReferencePricingActions.loadProductsReferencePrice),
      mergeMap(({ productCodes }) => {
        return this.apiService.getTopProductsPriceList(productCodes).pipe(
          map((response: any) => {
            return response.body.priceItems.map((priceObj) => {
              if (priceObj.message && priceObj.message !== 8 && priceObj.message !== 4 && priceObj.message !== 9) {
                return ProductReferencePricingActions.loadProductsReferencePriceFailure({
                  productCode: priceObj.code,
                  error: priceObj.message || 'Price fetch failed'
                });
              } else {
                return ProductReferencePricingActions.loadProductsReferencePriceSuccess({
                  productCode: priceObj.code,
                  priceData: priceObj.discountPriceData,
                  quotationFlag: priceObj.quotationFlag,
                  obsoleteFlag: priceObj.obsoleteFlag
                });
              }
            });
          }),
          catchError((error) =>
            of(ProductReferencePricingActions.loadProductsReferencePriceFailure({
              productCode: '',
              error: error
            }))
          ),
          mergeMap((actionsArray) => Array.isArray(actionsArray) ? from(actionsArray) : of(actionsArray))
        );
      }
      )
    )
  )
}